import {POPUP, PopupFloating, PopupTooltip} from "/@back@/commons/widgets/popups.js";
import {REG} from "/@lib@/commons/registry.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IBoxElement} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {BASIS} from "/@back@/commons/basis.js";
import {IView} from "/@lib@/commons/views.js";
import {LANG} from "/@lib@/commons/lang.js";
import {ACTION} from "/@lib@/commons/actions.js";


interface OBoxTooltipOptions {
	factory?: (owner: HTMLElement, tooltip: PopupTooltip) => IView | null,
	findFocusableOnShown?: boolean,
	/** Comportement à adopter avec l'attribut title :
	 * <ul>
	 *		<li>replaceTitle [comportement par défaut] : le tooltip <b>remplace</b> le title, qui ne sera donc pas affiché
	 *		<li>showOnlyIfNoTitle : le tooltip est affiché <b>SSi</b> la box ne possède pas de title
	 *		<li>showAll : le tooltip <b>et</b> le title sont affichés
	 * </ul>
	 */
	withTitle?: 'replaceTitle' | 'showOnlyIfNoTitle' | 'showAll',
}

interface OBoxFloatingOptions {
	/** click par défaut */
	display?: ('click' | 'over')[]
	factory?: (owner: HTMLElement, tooltip: PopupFloating) => IView | null,
	findFocusableOnShown?: boolean,
}

/**
 * Agregation pour les IElementWedlet : Affichage d'un tooltip (sur le mouseover)
 */
export function AgEltBoxTooltip(cls: any, opts: OBoxTooltipOptions): any {
	const options = Object.assign({
		findFocusableOnShown: false,
		withTitle: 'replaceTitle',
	}, opts);
	if (!options.factory) return;

	const proto = cls.prototype as IBoxElement;

	const connectedCallback = proto.connectedCallback;

	proto.connectedCallback = function (this: IBoxElement) {
		if (connectedCallback) connectedCallback.call(this);
		const reg = this.wedlet.wedMgr.reg;
		const sh = this.shadowRoot;
		if (sh) reg.installSkin("box-tt", sh);
		if (options.withTitle === 'replaceTitle')
			this.removeAttribute("title");

		if (options.withTitle !== 'showOnlyIfNoTitle' || !this.title) {
			POPUP.promiseTooltip(this, options.findFocusableOnShown ?
				(owner: HTMLElement, tooltip: PopupTooltip): IView | null => {
					tooltip.addEventListener('c-shown', () => {
						const focusbale = DOMSH.findFlatNext(tooltip.firstElementChild, tooltip, DOM.IS_focusable);
						if (focusbale) focusbale.focus();
					});
					return options.factory(owner, tooltip);
				} : options.factory,
				{hoverAllowed: true, skin: "c-popup-tooltip", skinOver: "box-tt/popup"});
		}
	};
	return cls;
}

/**
 * Agregation pour les IElementWedlet : Affichage d'un floating
 */
export function AgEltBoxFloating(cls: any, opts: OBoxFloatingOptions): any {
	const options: OBoxFloatingOptions = Object.assign({
		display: ['click'],
		findFocusableOnShown: false,
	}, opts);
	if (!options.factory) return;
	const proto = cls.prototype as IBoxElement;
	const connectedCallback = proto.connectedCallback;
	const disconnectedCallback = proto.disconnectedCallback;
	proto.connectedCallback = function (this: IBoxElement) {
		if (connectedCallback) connectedCallback.call(this);
		const reg = this.wedlet.wedMgr.reg;
		const sh = this.shadowRoot;
		if (sh) reg.installSkin("box-tt", sh);
		if (options.factory) {
			const openFct = (focusOnClose?: boolean) => {
				POPUP.showFloating((popup: PopupFloating) => {
					if (options.display.includes('click')) this.setAttribute("aria-pressed", "true");
					if (options.findFocusableOnShown) {
						popup.addEventListener('c-shown', () => {
							const focusable = DOMSH.findFlatNext(popup.firstElementChild, popup, DOM.IS_focusable);
							if (focusable) focusable.focus();
						});
					}
					(this as any)._boxTtPopupFloating = popup;
					popup.addEventListener('c-close', () => {
						(this as any)._boxTtPopupFloating = null;
						if (focusOnClose && DOM.IS_focusable(this)) this.focus();
						if (options.display.includes('click')) this.setAttribute("aria-pressed", "false");
					});
					return options.factory(this, popup);
				}, this, this, {closeOnBlur: true, skin: "c-popup-floating", skinOver: "box-tt/floating"});
			};

			if (options.display.includes('click')) {
				BASIS.makeClickable(this);
				this.setAttribute("role", "button");
				this.setAttribute("aria-pressed", "false");
				this.onclick = () => openFct(true);
			}
			if (options.display.includes('over')) {
				POPUP.mouseOverRetarderCaller(this, false, openFct);
				this.addEventListener('keypress', function (ev: KeyboardEvent) {
					if (LANG.in(ev.key, 'Enter', ' ') && !ACTION.isAnyControlPressed(ev)) {
						ev.stopImmediatePropagation();
						ev.preventDefault();
						openFct(true);
					}
				});
			}

			proto.disconnectedCallback = function (this: IBoxElement) {
				if (disconnectedCallback) disconnectedCallback.call(this);
				(this as any)._boxTtPopupFloating?.close();
			}
		}
		return cls;
	}
}


REG.reg.registerSkin("box-tt", 1, /* language=CSS */ `
	:host([role=button]) {
		cursor: pointer
	}
`);

REG.reg.registerSkin("box-tt/popup", 1, /* language=CSS */ `

`);